import React, { createContext, useContext, useEffect, useState } from "react";
import { userManager } from "./oidcConfig";
import type { SigninRedirectArgs, User } from "oidc-client-ts";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSigningOut: boolean;
  signinRedirect: (args?: SigninRedirectArgs) => void;
  signoutRedirect: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isSigningOut: false,
  signinRedirect: () => {},
  signoutRedirect: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const loadedUser = await userManager.getUser();
        if (mounted && loadedUser && !loadedUser.expired) {
          setUser(loadedUser);
        }
      } catch (err) {
        console.error("Error loading user:", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    initAuth();

    // Event handlers
    const handleUserLoaded = (user: User) => setUser(user);
    const handleUserUnloaded = () => setUser(null);
    const signOut = async () => {
      setIsSigningOut(true);
      try {
        await userManager.signoutRedirect();
      } catch (err) {
        console.error("Logout error:", err);
        setIsSigningOut(false);
      }
    };

    const handleAccessTokenExpired = async () => {
      console.warn("Access token expired, attempting silent renew...");
      try {
        const renewedUser = await userManager.signinSilent();
        setUser(renewedUser);
      } catch (err) {
        console.error("Silent renew failed, signing out:", err);
        await signOut();
      }
    };

    const handleUserSignedOut = async () => {
      console.warn("User was signed out from the IDP.");
      await signOut();
    };

    // Register event listeners
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addUserSignedOut(handleUserSignedOut);

    // Cleanup
    return () => {
      mounted = false;
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
    };
  }, []);

  const signinRedirect = (args?: SigninRedirectArgs) =>
    userManager.signinRedirect(args);
  const signoutRedirect = async () => {
    setIsSigningOut(true);
    try {
      await userManager.signoutRedirect();
    } catch (err) {
      console.error("Logout error:", err);
      setIsSigningOut(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isSigningOut,
        signinRedirect,
        signoutRedirect,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
